import React, { useCallback } from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import DialogComponent from '../../component/dialog/dialog.component';
import i18n from '../../i18n';
import { ReactComponent as CloseIcon } from '../../static/images/svg/mingcute_close-fill.svg';
import { ReactComponent as EpSuccessFilledIcon } from '../../static/images/svg/ep_success-filled.svg';
import { ReactComponent as TriangleDangerIcon } from '../../static/images/svg/jam_triangle-danger-f.svg';
import './menu-store-use-now-result-dialog.style.scss';

export type MenuStoreUseNowResultDialogProps = {
    /** 是否打开 */
    open: boolean;
    /** 关闭 */
    onClose: () => void;
    /** 副标题：菜单名 ± 日期（与 shell2-app `MenuStoreUseNowResultModal` subtitle） */
    subtitle: string;
    /** 正文行（已本地化；顺序与 App 一致） */
    lines: string[];
    /** 是否至少有门店同步成功（否则展示错误图标） */
    hadSuccessfulSync: boolean;
};

/**
 * 「立刻使用」分发结果汇总弹窗：文案与 shell2-app `MenuStoreUseNowResultModal` 对齐（标题「同步结果」+ 行文案键 + 确定）。
 */
export function MenuStoreUseNowResultDialog(props: MenuStoreUseNowResultDialogProps): React.ReactElement | null {
    const { open, onClose, subtitle, lines, hadSuccessfulSync } = props;

    /** 点击遮罩不关闭；Esc 可关闭 */
    const handleDialogClose = useCallback(
        (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
            if (reason === 'backdropClick') {
                return;
            }
            onClose();
        },
        [onClose],
    );

    const subtitleText = subtitle.trim() || '—';

    if (!open) {
        return null;
    }

    return (
        <DialogComponent
            className="menu-store-use-now-result-dialog"
            open={open}
            onClose={handleDialogClose}
            maxWidth={false}
            fullWidth
            aria-labelledby="menu-store-use-now-result-title"
            PaperProps={{
                className: 'menu-store-use-now-result-dialog__paper',
                elevation: 0,
                sx: {
                    maxWidth: '640px',
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'var(--fill-neutral-100)',
                    backgroundColor: 'var(--fill-neutral-100)',
                    border: '2px solid var(--stroke-primary-300)',
                },
            }}
            sx={[
                {
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'var(--overlay-800-a25)',
                    },
                    '& .MuiDialog-container > .MuiPaper-root': {
                        background: 'var(--fill-neutral-100)',
                        backgroundColor: 'var(--fill-neutral-100)',
                        border: '2px solid var(--stroke-primary-300)',
                        boxShadow: 'none',
                    },
                    '& .MuiDialogTitle-root': {
                        backgroundColor: 'var(--fill-neutral-100)',
                        paddingTop: '16px',
                        paddingLeft: '24px',
                        paddingRight: '24px',
                        paddingBottom: '0px',
                        width: '100%',
                        boxSizing: 'border-box',
                    },
                    '& .MuiDialogContent-root': {
                        paddingTop: '24px !important',
                    },
                    '& .dialog-footer': {
                        display: 'none',
                    },
                },
            ]}
            dialogContentStyle={{
                paddingTop: '24px',
                paddingLeft: '24px',
                paddingRight: '24px',
                paddingBottom: '24px',
            }}
            defaultHeaderProps={{
                titleAlign: 'left',
                isHiddenCloseIcon: true,
                onClose: () => onClose(),
                titleContent: (
                    <Box
                        className="menu-store-use-now-result-dialog__title-row"
                        sx={{ width: '100%', boxSizing: 'border-box' }}
                    >
                        <Box className="menu-store-use-now-result-dialog__title-block">
                            <Typography
                                id="menu-store-use-now-result-title"
                                component="h2"
                                className="menu-store-use-now-result-dialog__title"
                            >
                                {i18n.t('menu_store_use_now_result_title')}
                            </Typography>
                            <Typography component="p" className="menu-store-use-now-result-dialog__subtitle">
                                {subtitleText}
                            </Typography>
                        </Box>
                        <IconButton
                            type="button"
                            size="small"
                            onClick={onClose}
                            aria-label={i18n.t('extra_cost_text_close')}
                            className="menu-store-use-now-result-dialog__close"
                        >
                            <CloseIcon width={24} height={24} aria-hidden />
                        </IconButton>
                    </Box>
                ),
            }}
        >
            <Stack spacing={6} className="menu-store-use-now-result-dialog__main-stack">
                <Box className="menu-store-use-now-result-dialog__icon-wrap" aria-hidden>
                    {hadSuccessfulSync ? (
                        <EpSuccessFilledIcon className="menu-store-use-now-result-dialog__success-icon" />
                    ) : (
                        <TriangleDangerIcon className="menu-store-use-now-result-dialog__error-icon" width={80} height={80} />
                    )}
                </Box>

                <Stack spacing={6} className="menu-store-use-now-result-dialog__lines">
                    {lines.map((line, idx) => (
                        <Typography
                            key={`${idx}-${line.slice(0, 24)}`}
                            component="p"
                            className="menu-store-use-now-result-dialog__line"
                        >
                            {line}
                        </Typography>
                    ))}
                </Stack>

                <Button
                    type="button"
                    variant="contained"
                    disableElevation
                    fullWidth
                    className="menu-store-use-now-result-dialog__ok"
                    onClick={onClose}
                >
                    {i18n.t('menu_store_use_now_result_confirm')}
                </Button>
            </Stack>
        </DialogComponent>
    );
}

export default MenuStoreUseNowResultDialog;
